import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import {
  releaseAendern, releaseFreigeben, releaseHochladen, releaseLoeschen, releaseNurTest, releasesListe, releaseZurueckziehen,
} from '../api';
import { Bestaetigung, Kopieren, Seitenkopf } from '../komponenten/Bausteine';
import { datum, fehlertext, groesse } from '../utils/format';

const KANAL = {
  test: { text: 'Nur Testkunden', farbe: 'warning' },
  alle: { text: 'Alle Kunden', farbe: 'success' },
  zurueckgezogen: { text: 'Zurückgezogen', farbe: 'default' },
};

export default function Releases() {
  const [releases, setReleases] = useState(null);
  const [fehler, setFehler] = useState('');
  const [upload, setUpload] = useState(false);
  const [version, setVersion] = useState('');
  const [changelog, setChangelog] = useState('');
  const [datei, setDatei] = useState(null);
  const [fortschritt, setFortschritt] = useState(null);
  const [uploadFehler, setUploadFehler] = useState('');
  const [frage, setFrage] = useState(null);     // { art, release }
  const [notiz, setNotiz] = useState(null);     // Release, dessen Changelog bearbeitet wird

  const laden = useCallback(() => {
    releasesListe().then((r) => setReleases(r.data)).catch((e) => setFehler(fehlertext(e, 'Releases konnten nicht geladen werden.')));
  }, []);
  useEffect(laden, [laden]);

  const dateiGewaehlt = (f) => {
    setDatei(f || null);
    // "ProfiWare-1.6.175-Setup.exe" -> Versionsnummer gleich uebernehmen, tippen ist fehleranfaellig
    const m = /(\d+\.\d+\.\d+)/.exec(f?.name || '');
    if (m) setVersion(m[1]);
  };

  const hochladen = async () => {
    if (!version || !datei) { setUploadFehler('Versionsnummer und Installer-Datei werden gebraucht.'); return; }
    setUploadFehler('');
    setFortschritt(0);
    const formData = new FormData();
    formData.append('version', version);
    formData.append('changelog', changelog);
    formData.append('datei', datei);
    try {
      await releaseHochladen(formData, setFortschritt);
      setUpload(false); setVersion(''); setChangelog(''); setDatei(null);
      laden();
    } catch (e) {
      setUploadFehler(fehlertext(e, 'Upload fehlgeschlagen.'));
    } finally {
      setFortschritt(null);
    }
  };

  const tun = async (fn, r) => {
    try { await fn(r.id); setFehler(''); laden(); } catch (e) { setFehler(fehlertext(e)); }
  };

  return (
    <Box>
      <Seitenkopf titel="Releases" untertitel="Eine neue Version geht zuerst nur an Testkunden und erst nach der Freigabe an alle.">
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => { setUploadFehler(''); setUpload(true); }}>Neue Version hochladen</Button>
      </Seitenkopf>
      {fehler && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFehler('')}>{fehler}</Alert>}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Freigabe</TableCell>
                <TableCell>Verteilt</TableCell>
                <TableCell>Hochgeladen</TableCell>
                <TableCell>Datei</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(releases || []).map((r) => {
                const abgeloest = r.kanal === 'alle' && r.abgeloest;
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700 }}>{r.version}</Typography>
                      <Typography
                        variant="body2" color="text.secondary" onClick={() => setNotiz({ ...r, text: r.changelog ?? '' })}
                        sx={{ cursor: 'pointer', minWidth: 200, maxWidth: 340, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
                      >
                        {r.changelog || 'Änderungen eintragen'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={abgeloest ? 'default' : KANAL[r.kanal]?.farbe} label={abgeloest ? 'Abgelöst' : KANAL[r.kanal]?.text} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130 }}>
                      {r.kanal === 'zurueckgezogen' ? '–' : (
                        <>
                          <Typography variant="body2">{r.verteilt} von {r.zielgruppe}</Typography>
                          <LinearProgress variant="determinate" value={r.zielgruppe ? (r.verteilt / r.zielgruppe) * 100 : 0} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{datum(r.veroeffentlicht_am)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.datei_vorhanden ? groesse(r.groesse) : <Typography variant="body2" color="error">Datei fehlt</Typography>}
                      {r.sha256 && (
                        <Tooltip title={`SHA-256: ${r.sha256}`}>
                          <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                            Prüfsumme<Kopieren text={r.sha256} titel="Prüfsumme kopieren" />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {r.kanal === 'test' && <Button size="small" variant="contained" sx={{ mr: 1 }} onClick={() => setFrage({ art: 'freigeben', release: r })}>Für alle freigeben</Button>}
                      {r.kanal === 'alle' && !r.abgeloest && <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => tun(releaseNurTest, r)}>Nur Testkunden</Button>}
                      {r.kanal !== 'zurueckgezogen' && <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => setFrage({ art: 'zurueck', release: r })}>Zurückziehen</Button>}
                      {r.kanal === 'zurueckgezogen' && <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => tun(releaseNurTest, r)}>Wieder anbieten</Button>}
                      <Button size="small" color="error" onClick={() => setFrage({ art: 'loeschen', release: r })}>Löschen</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {releases && releases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    Noch keine Version hochgeladen. Der Installer liegt nach dem Build unter C:\ProfiWare-dist\installer.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Angeboten wird immer die höchste freigegebene Versionsnummer. „Verteilt“ zählt die Installationen mit Lizenz, die mindestens diese Version haben.
      </Typography>

      <Dialog open={upload} onClose={fortschritt != null ? undefined : () => setUpload(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neue Version hochladen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {uploadFehler && <Alert severity="error">{uploadFehler}</Alert>}
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={fortschritt != null}>
            {datei ? `${datei.name} (${groesse(datei.size)})` : 'Installer-Datei wählen'}
            <input id="release-datei" type="file" hidden accept=".exe" onChange={(e) => dateiGewaehlt(e.target.files[0])} />
          </Button>
          <TextField
            id="release-version" label="Versionsnummer" value={version} onChange={(e) => setVersion(e.target.value)}
            placeholder="1.6.175" helperText="Wird aus dem Dateinamen übernommen. Muss zur Version im Installer passen."
          />
          <TextField id="release-changelog" label="Was ist neu?" value={changelog} onChange={(e) => setChangelog(e.target.value)} multiline minRows={3} />
          {fortschritt != null && (
            <Box>
              <Typography variant="body2" color="text.secondary">{fortschritt < 100 ? `Lädt hoch … ${fortschritt} %` : 'Wird gespeichert und geprüft …'}</Typography>
              <LinearProgress variant="determinate" value={fortschritt} sx={{ mt: 0.5 }} />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">Die Version geht zuerst nur an Testkunden.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpload(false)} disabled={fortschritt != null}>Abbrechen</Button>
          <Button variant="contained" onClick={hochladen} disabled={fortschritt != null}>Hochladen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!notiz} onClose={() => setNotiz(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Was ist neu in {notiz?.version}?</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField id="release-notiz" fullWidth multiline minRows={4} autoFocus value={notiz?.text ?? ''} onChange={(e) => setNotiz({ ...notiz, text: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotiz(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={async () => { try { await releaseAendern(notiz.id, notiz.text); setNotiz(null); laden(); } catch (e) { setFehler(fehlertext(e)); setNotiz(null); } }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Bestaetigung
        offen={frage?.art === 'freigeben'} titel={`Version ${frage?.release.version} für alle freigeben?`} knopf="Für alle freigeben" farbe="primary"
        text="Alle Kunden bekommen diese Version beim nächsten Check-in als Update angeboten."
        onNein={() => setFrage(null)} onJa={async () => { await releaseFreigeben(frage.release.id); setFrage(null); laden(); }}
      />
      <Bestaetigung
        offen={frage?.art === 'zurueck'} titel={`Version ${frage?.release.version} zurückziehen?`} knopf="Zurückziehen" farbe="warning"
        text="Die Version wird niemandem mehr angeboten und lässt sich nicht mehr herunterladen. Wer sie schon installiert hat, behält sie."
        onNein={() => setFrage(null)} onJa={async () => { await releaseZurueckziehen(frage.release.id); setFrage(null); laden(); }}
      />
      <Bestaetigung
        offen={frage?.art === 'loeschen'} titel={`Version ${frage?.release.version} löschen?`} knopf="Version löschen"
        text="Eintrag und Installer-Datei werden vom Portal entfernt. Das lässt sich nicht rückgängig machen."
        onNein={() => setFrage(null)} onJa={async () => { await releaseLoeschen(frage.release.id); setFrage(null); laden(); }}
      />
    </Box>
  );
}
